import React, { useEffect, useRef, useState } from 'react';

import type { CodeBlockRedlineItem, UUID } from '@aline/common';
import { Typography } from '@mui/material';
import { ChevronDown } from 'lucide-react';
import { makeStyles } from 'tss-react/mui';

import { PlaybookAnimatedClip } from './playbook-animated-clip';
import type { PlaybookCategory, PlaybookRule } from './playbook-run-state';
import { getCategorySummary } from './playbook-run-state';
import { PLAYBOOK_CHIP_LABELS, PLAYBOOK_STATUS_CHIPS, PlaybookChip } from './PlaybookChip';
import type { PlaybookRuleActions } from './PlaybookRuleCard';
import { PlaybookRuleCard } from './PlaybookRuleCard';
import { useIsStuck } from './use-is-stuck';

const useStyles = makeStyles()(theme => ({
  chevron: {
    color: theme.appColors.secondaryType,
    flexShrink: 0,
    height: 18,
    transition: 'transform 0.15s',
    width: 18,
  },
  chevronOpen: {
    transform: 'rotate(180deg)',
  },
  /**
   * While the clip is animating, sticky titles would stack against the
   * overflow:hidden box. Park them in flow until the spring rests.
   */
  clippingSticky: {
    '& [data-playbook-sticky-title]': {
      position: 'static',
    },
  },
  empty: {
    color: theme.appColors.secondaryType,
    fontSize: 14,
    padding: theme.spacing(1),
  },
  /**
   * A hairline under the whole category is the only boundary it gets: the
   * rules and positions inside carry enough chrome of their own. No extra
   * padding below — that stacked with the next header's padding and a list
   * gap, and put a hole between the line and the following category name.
   */
  group: {
    borderBottom: `1px solid ${theme.appColors.rule}`,
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    alignItems: 'center',
    background: theme.palette.background.paper,
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'space-between',
    padding: theme.spacing(1.25, 0),
    position: 'sticky',
    textAlign: 'left',
    top: 0,
    width: '100%',
    // Above the rule titles that park beneath it.
    zIndex: 2,
  },
  headerRight: {
    alignItems: 'center',
    display: 'flex',
    flexShrink: 0,
    gap: theme.spacing(1),
  },
  /**
   * Hairline under the pinned category so it doesn't melt into the rule
   * title parked beneath it. Shadow, not border, so stick doesn't shift layout.
   */
  headerStuck: {
    boxShadow: `0 1px 0 ${theme.appColors.rule}`,
  },
  // The name wraps rather than truncates, however long it is.
  name: {
    fontSize: 16,
    fontWeight: 600,
    overflowWrap: 'anywhere',
  },
  // Rules divide themselves with hairlines, so no gap between them.
  rules: {
    display: 'flex',
    flexDirection: 'column',
  },
  sentinel: {
    height: 1,
    marginBottom: -1,
    pointerEvents: 'none',
    visibility: 'hidden',
  },
}));

/** A single-line header, until the real one has been measured. */
const FALLBACK_HEADER_HEIGHT = 44;

function SummaryChip({ rules }: { rules: PlaybookRule[] }): JSX.Element {
  const summary = getCategorySummary(rules);
  if (summary.type === 'count') {
    return (
      <PlaybookChip
        label={
          summary.count === 0
            ? PLAYBOOK_CHIP_LABELS.noRules
            : `${summary.count} rule${summary.count === 1 ? '' : 's'}`
        }
        tone="neutral"
      />
    );
  }

  const { label, tone } = PLAYBOOK_STATUS_CHIPS[summary.type];
  return <PlaybookChip label={label} tone={tone} />;
}

export function PlaybookCategoryCard({
  actions,
  category,
  displayRules,
  expanded,
  getIsStale,
  onToggleExpanded,
  pendingAcceptedSuggestionIds,
  playbookId,
}: {
  actions: PlaybookRuleActions;
  category: PlaybookCategory;
  displayRules: { rule: PlaybookRule; ruleNumber: number }[];
  expanded: boolean;
  getIsStale: (previewCards: CodeBlockRedlineItem[]) => boolean;
  onToggleExpanded: () => void;
  pendingAcceptedSuggestionIds: ReadonlySet<string>;
  playbookId: UUID;
}): JSX.Element {
  const { classes, cx } = useStyles();
  const { sentinelRef, stuck } = useIsStuck(0);
  const headerRef = useRef<HTMLButtonElement | null>(null);
  const [headerHeight, setHeaderHeight] = useState(FALLBACK_HEADER_HEIGHT);

  /**
   * Rule titles stick directly beneath this header, so they need its live
   * height: a long category name wraps, and a hidden panel measures zero.
   */
  useEffect(() => {
    const header = headerRef.current;
    if (!header) return;

    const observer = new ResizeObserver(() => {
      if (header.offsetHeight > 0) setHeaderHeight(header.offsetHeight);
    });
    observer.observe(header);
    return () => observer.disconnect();
  }, []);

  return (
    <div className={classes.group}>
      <div ref={sentinelRef} className={classes.sentinel} />
      <button
        ref={headerRef}
        aria-expanded={expanded}
        className={cx(classes.header, stuck && classes.headerStuck)}
        type="button"
        onClick={onToggleExpanded}
      >
        <Typography className={classes.name}>{category.name || 'Untitled category'}</Typography>
        <div className={classes.headerRight}>
          {/* An open category shows its rules' own statuses, not a roll-up. */}
          {!expanded && <SummaryChip rules={displayRules.map(({ rule }) => rule)} />}
          <ChevronDown className={cx(classes.chevron, expanded && classes.chevronOpen)} />
        </div>
      </button>
      <PlaybookAnimatedClip clippingClassName={classes.clippingSticky} expanded={expanded}>
        <div className={classes.rules}>
          {displayRules.length === 0 && (
            <Typography className={classes.empty}>No rules in this category yet.</Typography>
          )}
          {displayRules.map(({ rule, ruleNumber }) => (
            <PlaybookRuleCard
              key={rule.id}
              actions={actions}
              getIsStale={getIsStale}
              pendingAcceptedSuggestionIds={pendingAcceptedSuggestionIds}
              playbookId={playbookId}
              rule={rule}
              ruleNumber={ruleNumber}
              stickyTop={headerHeight}
            />
          ))}
        </div>
      </PlaybookAnimatedClip>
    </div>
  );
}
